import React, { useEffect, useState } from "react";
import ReactEcharts from "echarts-for-react";
import {
  Flex,
  Box,
  Text,
  Skeleton,
  SkeletonText,
  Select,
} from "@chakra-ui/react";
import ExportButton from "components/Reports/ExportCSV";
import { useDispatch, useSelector } from "react-redux";
import { fetchStatusDistributionAction } from "state/actions/analyticsActions";

const tables = [
  "users",
  "packages",
  "deliveries",
  "transactions",
  "drivers",
  "businesses",
  "vehicles",
  "reviews",
  "help_topics",
  "chats",
  "faqs",
  "notifications",
];

const PieChart = ({
  title = "Status Distribution",
  exportFileName = "pie_chart_data",
  showExportButton = true,
}) => {
  const dispatch = useDispatch();
  const [selectedTable, setSelectedTable] = useState("packages");
  const { statusDistribution, loading, error } = useSelector(
    (state) => state.analytics
  );
  const [hasFetched, setHasFetched] = useState({});

  useEffect(() => {
    if (!hasFetched[selectedTable]) {
      dispatch(fetchStatusDistributionAction(selectedTable));
      setHasFetched((prev) => ({ ...prev, [selectedTable]: true }));
    }
  }, [dispatch, selectedTable, hasFetched]);


  const gradientColors = [
    "#071D54",
    "#102A70",
    "#1A388C",
    "#2445A8",
    "#2D52C4",
    "#376FE0",
    "#418CFC",
    "#5AA0FF",
  ];

  // Helper function to capitalize the first letter of each word in a string
  const capitalizeFirstLetter = (str) => {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Prepare the data for the pie chart (converting it into a format suitable for echarts)
  const chartData = Array.isArray(statusDistribution[selectedTable])
    ? statusDistribution[selectedTable].map((item) => ({
        name: capitalizeFirstLetter(item.status.split(".")[1]),
        value: item.count,
      }))
    : Object.entries(statusDistribution[selectedTable] || {}).map(
        ([status, count]) => ({
          name: capitalizeFirstLetter(status.replace("_", " ")), 
          value: count,
        })
      );
  const capitalizedSelectedTable = capitalizeFirstLetter(selectedTable);
  const option = {
    title: {
      text: `${title}  (${capitalizedSelectedTable})`,
      left: "left",
      textStyle: {
        fontWeight: "500",
        fontSize: 16,
        color: "#262626",
      },
    },
    tooltip: { trigger: "item" },
    legend: { orient: "horizontal", left: "center", bottom: "0%" },
    series: [
      {
        name: selectedTable,
        type: "pie",
        radius: "50%",
        data: chartData.length
          ? chartData
          : [{ value: 1, name: "No Data" }],
        itemStyle: {
          color: (params) =>
            chartData.length
              ? gradientColors[params.dataIndex % gradientColors.length]
              : "#6ECAF0",
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
        label: {
          position: "outside",
          formatter: chartData.length ? "{b}: {d}%" : "",
        },
      },
    ],
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return <Text color="red.500">Error loading data: {error}</Text>;
  }

  return (
    <Flex
      w="100%"
      h="100%"
      boxShadow="0px 4px 24px 0px #0000001F"
      borderRadius="12px"
      py={4}
      px={5}
      direction="column"
    >
      <Flex justify="space-between" alignItems="center" mb={4} w="100%">
        <Select
          w="200px"
          value={selectedTable}
          onChange={(e) => setSelectedTable(e.target.value)}
        >
          {tables.map((table) => (
            <option key={table} value={table}>
              {table.charAt(0).toUpperCase() + table.slice(1)}
            </option>
          ))}
        </Select>

        {showExportButton && (
          <ExportButton data={chartData} fileName={exportFileName} />
        )}
      </Flex>
      <ReactEcharts option={option} style={{ width: "100%", height: 400 }} />
    </Flex>
  );
};

const LoadingSkeleton = () => (
  <Flex w="100%">
    <Box w="100%" mt={6}>
      <Skeleton w="40%" mb={4} height="4" />
      <SkeletonText noOfLines={6} spacing="4" skeletonHeight="4" />
    </Box>
  </Flex>
);

export default PieChart;
